import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { IComputedValue } from "mobx";
import React, { useRef, useState } from "react";
import { Position, Rect } from "../Types";
import { canvasViewport } from "./CanvasState";
import { roundPositionToGrid } from "./SandboxUtils";

export function PaneResizers({
  rectComputed,
  constrainSize,
  shouldRoundToGrid,
  onUpdate,
}: {
  rectComputed: IComputedValue<Rect>;
  constrainSize?: (size: Position) => Position;
  shouldRoundToGrid: boolean;
  onUpdate: (rect: Rect) => void;
}) {
  const isResizingRef = useRef(false);
  const bindDrag = useDrag(({ first, last, args, initial, xy, memo }) => {
    const side: string = args[0];
    if (first) {
      const [startX, startY, startWidth, startHeight] = rectComputed.get();
      isResizingRef.current = true;
      return { startX, startY, startWidth, startHeight };
    } else if (last) {
      setIsMouseOverCorner(undefined);
      isResizingRef.current = false;
    } else {
      const { startX, startY, startWidth, startHeight } = memo;
      const canvasZoom = canvasViewport.zoom;
      let diffWidth =
        side.includes("right") || side.includes("left")
          ? ((xy[0] - initial[0]) / canvasZoom) *
            (side.includes("left") ? -1 : 1)
          : 0;
      let diffHeight =
        side.includes("bottom") || side.includes("top")
          ? ((xy[1] - initial[1]) / canvasZoom) *
            (side.includes("top") ? -1 : 1)
          : 0;
      if (shouldRoundToGrid) {
        [diffWidth, diffHeight] = roundPositionToGrid(
          [diffWidth, diffHeight],
          1
        );
      }
      let [width, height] = [startWidth + diffWidth, startHeight + diffHeight];
      if (constrainSize !== undefined) {
        [width, height] = constrainSize([width, height]);
      }
      onUpdate([
        side.includes("left") ? startX - (width - startWidth) : startX,
        side.includes("top") ? startY - (height - startHeight) : startY,
        width,
        height,
      ]);
    }
    return memo;
  });

  const [isMouseOverCorner, setIsMouseOverCorner] = useState<
    "bottom-left" | "bottom-right" | undefined
  >(undefined);

  return (
    <>
      <div
        {...bindDrag("left")}
        className="w-3 absolute top-0 bottom-0 -left-2 z-1 cursor-ew-resize group touch-none overflow-hidden"
      >
        <div
          className={classNames(
            "absolute top-0 bottom-0 left-[7px] w-2 border-l transition-opacity border-neutral-300 rounded",
            isMouseOverCorner?.includes("left")
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          )}
        />
      </div>
      <div
        {...bindDrag("right")}
        className="w-3 absolute top-0 bottom-0 -right-2 z-1 cursor-ew-resize group touch-none overflow-hidden"
      >
        <div
          className={classNames(
            "absolute top-0 bottom-0 right-[7px] w-2 border-r transition-opacity border-neutral-300 rounded",
            isMouseOverCorner?.includes("right")
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          )}
        />
      </div>
      <div
        {...bindDrag("bottom")}
        className="h-3 absolute -bottom-2 left-0 right-0 z-1 cursor-ns-resize group touch-none overflow-hidden"
      >
        <div
          className={classNames(
            "absolute left-0 right-0 bottom-[7px] h-2 border-b transition-opacity border-neutral-300 rounded",
            isMouseOverCorner?.includes("bottom")
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100"
          )}
        />
      </div>
      <div
        {...bindDrag("bottom-left")}
        onMouseOver={() => {
          setIsMouseOverCorner("bottom-left");
        }}
        onMouseOut={() => {
          if (!isResizingRef.current) {
            setIsMouseOverCorner(undefined);
          }
        }}
        className="w-4 h-4 absolute -bottom-2 -left-2 z-1 cursor-nesw-resize touch-none"
      />
      <div
        {...bindDrag("bottom-right")}
        onMouseOver={() => {
          setIsMouseOverCorner("bottom-right");
        }}
        onMouseOut={() => {
          if (!isResizingRef.current) {
            setIsMouseOverCorner(undefined);
          }
        }}
        className="w-4 h-4 absolute -bottom-2 -right-2 z-1 cursor-nwse-resize touch-none"
      />
    </>
  );
}
